// Central data source for all 6 breeds.
// Editing this file (or adding a new object) will automatically create/refresh a full breed profile
// across the entire website. No layout or code changes required for future breeds.

const IMG = {
    // AI-generated cinematic imagery, all cow-only, no humans, no ropes/fences.
    hf: "/generated/hf.jpg",
    jersey: "/generated/jersey.jpg",
    black: "/generated/black.jpg",
    gir: "/generated/gir.jpg",
    kankrej: "/generated/kankrej.jpg",
    hallikar: "/generated/hallikar.jpg",
    // Alt/gallery variants — reuse editorial calf/mother-calf imagery.
    girAlt: "/generated/calf_only.jpg",
    girAlt2: "/generated/feature.jpg",
    kankrejAlt: "/generated/about.jpg",
    hallikarAlt: "/generated/calf_only.jpg",
    hallikarAlt2: "/generated/feature.jpg",
    coorg: "/generated/hero.jpg",
    motherCalf: "/generated/about.jpg",
    calf: "/generated/calf_only.jpg",
    // Landscape extras (Unsplash editorial)
    landscape1: "https://images.unsplash.com/photo-1770371474286-bc6c5098ed5f?w=1600&q=85&auto=format&fit=crop",
    landscape2: "https://images.unsplash.com/photo-1500595046743-cd271d694d30?w=1600&q=85&auto=format&fit=crop",
};

// Reference values — realistic Karnataka averages. Edit here to replace with verified farm data.
export const breeds = [
    {
        slug: "hf",
        name: "Holstein Friesian",
        shortName: "HF",
        tagline: "The high-volume commercial dairy cow.",
        origin: "Netherlands & Northern Germany",
        classification: "Exotic dairy breed",
        color: "Distinct black and white patches",
        image: IMG.hf,
        objectPosition: "72% 55%",
        heroImage: IMG.hf,
        gallery: [IMG.hf, IMG.landscape1, IMG.landscape2, IMG.motherCalf],
        quickFacts: [
            { label: "Avg. Yield", value: "20–25 L/day" },
            { label: "Milk Fat", value: "3.0%" },
            { label: "Lifespan", value: "6–8 lactations" },
            { label: "Climate", value: "Cool preferred" },
        ],
        production: {
            avgYield: "20–25 L/day",
            peakYield: "35–40 L/day",
            lactationPeriod: "305 days",
            milkPerLactation: "6,500 – 7,500 L",
            milkFat: "3.0%",
            snf: "8.5%",
            protein: "3.2%",
            suitableFor: ["Drinking Milk", "Paneer", "Curd", "Cheese"],
        },
        economics: {
            calf: "₹25,000 – ₹35,000",
            heifer: "₹55,000 – ₹75,000",
            firstCalving: "₹80,000 – ₹1,00,000",
            lactating: "₹90,000 – ₹1,20,000",
            pregnant: "₹85,000 – ₹1,10,000",
        },
        feeding: {
            greenFodder: "30 kg/day",
            dryFodder: "8 kg/day",
            concentrate: "8 kg/day",
            mineral: "100 g/day",
            salt: "60 g/day",
            water: "80 L/day",
        },
        feedingCost: [
            { feed: "Green Fodder", qty: "30 kg", daily: 90, monthly: 2700, yearly: 32850 },
            { feed: "Dry Fodder", qty: "8 kg", daily: 80, monthly: 2400, yearly: 29200 },
            { feed: "Concentrate", qty: "8 kg", daily: 240, monthly: 7200, yearly: 87600 },
            { feed: "Mineral Mixture", qty: "100 g", daily: 12, monthly: 360, yearly: 4380 },
            { feed: "Salt", qty: "60 g", daily: 3, monthly: 90, yearly: 1095 },
        ],
        totalDailyFeedCost: 425,
        labour: {
            manHours: "2.5 hrs/cow/day",
            breakdown: [
                { task: "Feeding", time: "45 min" },
                { task: "Cleaning", time: "35 min" },
                { task: "Milking", time: "40 min" },
                { task: "Grooming", time: "15 min" },
                { task: "Health Inspection", time: "10 min" },
                { task: "Record Keeping", time: "5 min" },
            ],
        },
        housing: {
            coveredArea: "40 sqft/cow",
            openArea: "80 sqft/cow",
            ventilation: "Cross ventilation with fans essential",
            floor: "Grooved concrete with rubber mats",
            drainage: "Sloped 2% toward central drain",
            bedding: "Rice straw or sand",
        },
        health: {
            diseaseResistance: "Moderate — sensitive to tropical stress",
            commonDiseases: ["Mastitis", "Milk Fever", "Ketosis"],
            heatStress: "High susceptibility above 28°C",
            tickResistance: "Low",
            mastitis: "High susceptibility",
            veterinary: "Monthly veterinary check-up recommended",
        },
        reproduction: {
            firstCalving: "24–30 months",
            gestation: "280 days",
            calvingInterval: "13–14 months",
            fertility: "Moderate under Indian conditions",
            insemination: "AI widely used with sexed semen",
            natural: "Rarely practiced commercially",
        },
        environment: {
            heat: "Low tolerance",
            rain: "Moderate — shelter required",
            cold: "High tolerance",
            humidity: "Low tolerance",
            regions: "Chikmagalur, Hassan, Kodagu — cooler ghats of Karnataka",
        },
        business: ["Medium Farms", "Commercial Farms", "High Volume Dairy"],
        ratings: {
            milkYield: 95, milkQuality: 65, feedEfficiency: 78,
            maintenanceCost: 55, diseaseResistance: 45, heatTolerance: 35,
            labourRequirement: 55, profitability: 82, beginnerFriendly: 45,
        },
        recommendation: {
            bestFor: "Large-scale commercial dairies in cooler Karnataka regions with cold-chain access.",
            notFor: "Small backyard farmers, humid coastal belts, and low-input operations.",
            monthlyOpCost: "₹18,000 – ₹22,000 per cow",
            monthlyMilk: "700 – 850 L",
            expectedROI: "24 – 30 months (with organized market access)",
            advantages: [
                "Highest daily yield among all breeds",
                "Predictable production curve",
                "Well-documented management protocols",
            ],
            challenges: [
                "Heat sensitivity — needs cooling infrastructure",
                "Higher veterinary and feed costs",
                "Requires disciplined management",
            ],
        },
    },
    {
        slug: "jersey",
        name: "Jersey",
        shortName: "Jersey",
        tagline: "Rich, buttery milk — the connoisseur's cow.",
        origin: "Jersey Island, English Channel",
        classification: "Exotic dairy breed",
        color: "Light fawn to dark brown with white markings",
        image: IMG.jersey,
        objectPosition: "55% 30%",
        heroImage: IMG.jersey,
        gallery: [IMG.jersey, IMG.landscape2, IMG.landscape1, IMG.calf],
        quickFacts: [
            { label: "Avg. Yield", value: "12–15 L/day" },
            { label: "Milk Fat", value: "3.5%" },
            { label: "Lifespan", value: "7–9 lactations" },
            { label: "Climate", value: "Adaptable" },
        ],
        production: {
            avgYield: "12–15 L/day",
            peakYield: "22–25 L/day",
            lactationPeriod: "305 days",
            milkPerLactation: "4,500 – 5,500 L",
            milkFat: "3.5%",
            snf: "9.2%",
            protein: "3.8%",
            suitableFor: ["Ghee", "Butter", "Paneer", "Curd", "Cheese"],
        },
        economics: {
            calf: "₹18,000 – ₹28,000",
            heifer: "₹40,000 – ₹55,000",
            firstCalving: "₹60,000 – ₹75,000",
            lactating: "₹70,000 – ₹90,000",
            pregnant: "₹65,000 – ₹85,000",
        },
        feeding: {
            greenFodder: "25 kg/day",
            dryFodder: "6 kg/day",
            concentrate: "6 kg/day",
            mineral: "80 g/day",
            salt: "50 g/day",
            water: "60 L/day",
        },
        feedingCost: [
            { feed: "Green Fodder", qty: "25 kg", daily: 75, monthly: 2250, yearly: 27375 },
            { feed: "Dry Fodder", qty: "6 kg", daily: 60, monthly: 1800, yearly: 21900 },
            { feed: "Concentrate", qty: "6 kg", daily: 180, monthly: 5400, yearly: 65700 },
            { feed: "Mineral Mixture", qty: "80 g", daily: 10, monthly: 300, yearly: 3650 },
            { feed: "Salt", qty: "50 g", daily: 2, monthly: 60, yearly: 730 },
        ],
        totalDailyFeedCost: 327,
        labour: {
            manHours: "2.0 hrs/cow/day",
            breakdown: [
                { task: "Feeding", time: "35 min" },
                { task: "Cleaning", time: "30 min" },
                { task: "Milking", time: "30 min" },
                { task: "Grooming", time: "12 min" },
                { task: "Health Inspection", time: "8 min" },
                { task: "Record Keeping", time: "5 min" },
            ],
        },
        housing: {
            coveredArea: "35 sqft/cow",
            openArea: "70 sqft/cow",
            ventilation: "Natural cross ventilation adequate",
            floor: "Grooved concrete with rubber mats",
            drainage: "Sloped 2% toward central drain",
            bedding: "Paddy straw",
        },
        health: {
            diseaseResistance: "Better than HF in tropical conditions",
            commonDiseases: ["Mastitis", "Milk Fever"],
            heatStress: "Moderate — comfortable up to 32°C",
            tickResistance: "Moderate",
            mastitis: "Moderate susceptibility",
            veterinary: "Bi-monthly veterinary check-up",
        },
        reproduction: {
            firstCalving: "22–26 months",
            gestation: "279 days",
            calvingInterval: "12–13 months",
            fertility: "Good",
            insemination: "AI preferred",
            natural: "Practiced in smaller farms",
        },
        environment: {
            heat: "Moderate tolerance",
            rain: "Good tolerance",
            cold: "High tolerance",
            humidity: "Moderate tolerance",
            regions: "Mysuru, Mandya, Tumakuru, Kolar — most of Karnataka's plateau",
        },
        business: ["Small Farms", "Medium Farms", "Commercial Farms", "Organic Dairy"],
        ratings: {
            milkYield: 72, milkQuality: 92, feedEfficiency: 88,
            maintenanceCost: 68, diseaseResistance: 70, heatTolerance: 62,
            labourRequirement: 70, profitability: 86, beginnerFriendly: 78,
        },
        recommendation: {
            bestFor: "Farmers targeting ghee, paneer and premium milk markets across Karnataka.",
            notFor: "Farms optimizing purely for volume against fluid milk contracts.",
            monthlyOpCost: "₹14,000 – ₹17,000 per cow",
            monthlyMilk: "450 – 550 L",
            expectedROI: "20 – 26 months",
            advantages: [
                "Highest fat and SNF among common dairy breeds",
                "Adaptable to Karnataka climate",
                "Excellent feed conversion efficiency",
            ],
            challenges: [
                "Lower total volume than HF",
                "Small body frame — care during calving",
                "Milk fat pricing must be available locally",
            ],
        },
    },
    {
        slug: "all-black",
        name: "All Black",
        shortName: "All Black",
        tagline: "Robust cross-bred striking pure-black cows.",
        origin: "Selective Indian cross-breeding programs",
        classification: "Cross-bred (HF × Indigenous)",
        color: "Solid glossy black",
        image: IMG.black,
        objectPosition: "62% 40%",
        heroImage: IMG.black,
        gallery: [IMG.black, IMG.landscape1, IMG.motherCalf, IMG.landscape2],
        quickFacts: [
            { label: "Avg. Yield", value: "12–15 L/day" },
            { label: "Milk Fat", value: "3.5%" },
            { label: "Lifespan", value: "6–8 lactations" },
            { label: "Climate", value: "Tropical adapted" },
        ],
        production: {
            avgYield: "12–15 L/day",
            peakYield: "26–30 L/day",
            lactationPeriod: "300 days",
            milkPerLactation: "5,000 – 6,000 L",
            milkFat: "3.5%",
            snf: "8.8%",
            protein: "3.4%",
            suitableFor: ["Drinking Milk", "Paneer", "Curd", "Ghee"],
        },
        economics: {
            calf: "₹22,000 – ₹30,000",
            heifer: "₹50,000 – ₹65,000",
            firstCalving: "₹70,000 – ₹90,000",
            lactating: "₹85,000 – ₹1,05,000",
            pregnant: "₹78,000 – ₹98,000",
        },
        feeding: {
            greenFodder: "28 kg/day",
            dryFodder: "7 kg/day",
            concentrate: "7 kg/day",
            mineral: "90 g/day",
            salt: "55 g/day",
            water: "70 L/day",
        },
        feedingCost: [
            { feed: "Green Fodder", qty: "28 kg", daily: 84, monthly: 2520, yearly: 30660 },
            { feed: "Dry Fodder", qty: "7 kg", daily: 70, monthly: 2100, yearly: 25550 },
            { feed: "Concentrate", qty: "7 kg", daily: 210, monthly: 6300, yearly: 76650 },
            { feed: "Mineral Mixture", qty: "90 g", daily: 11, monthly: 330, yearly: 4015 },
            { feed: "Salt", qty: "55 g", daily: 3, monthly: 90, yearly: 1095 },
        ],
        totalDailyFeedCost: 378,
        labour: {
            manHours: "2.2 hrs/cow/day",
            breakdown: [
                { task: "Feeding", time: "40 min" },
                { task: "Cleaning", time: "32 min" },
                { task: "Milking", time: "35 min" },
                { task: "Grooming", time: "13 min" },
                { task: "Health Inspection", time: "8 min" },
                { task: "Record Keeping", time: "5 min" },
            ],
        },
        housing: {
            coveredArea: "38 sqft/cow",
            openArea: "75 sqft/cow",
            ventilation: "Cross ventilation with fans",
            floor: "Grooved concrete with rubber mats",
            drainage: "Sloped 2% toward central drain",
            bedding: "Rice straw",
        },
        health: {
            diseaseResistance: "Better than pure HF",
            commonDiseases: ["Mastitis", "Milk Fever"],
            heatStress: "Moderate — needs shade and fans",
            tickResistance: "Moderate",
            mastitis: "Moderate susceptibility",
            veterinary: "Monthly veterinary check-up",
        },
        reproduction: {
            firstCalving: "24–28 months",
            gestation: "280 days",
            calvingInterval: "13–14 months",
            fertility: "Good",
            insemination: "AI widely used",
            natural: "Occasionally practiced",
        },
        environment: {
            heat: "Moderate tolerance",
            rain: "Good tolerance",
            cold: "High tolerance",
            humidity: "Moderate tolerance",
            regions: "Bengaluru rural, Kolar, Chikkaballapur, Ramanagara",
        },
        business: ["Medium Farms", "Commercial Farms", "High Volume Dairy"],
        ratings: {
            milkYield: 82, milkQuality: 74, feedEfficiency: 80,
            maintenanceCost: 62, diseaseResistance: 62, heatTolerance: 55,
            labourRequirement: 65, profitability: 84, beginnerFriendly: 62,
        },
        recommendation: {
            bestFor: "Farmers seeking a balance of HF volume and improved Indian tropical hardiness.",
            notFor: "Very small farms with limited fodder resources.",
            monthlyOpCost: "₹16,000 – ₹19,000 per cow",
            monthlyMilk: "540 – 660 L",
            expectedROI: "22 – 28 months",
            advantages: [
                "Higher yield than indigenous breeds",
                "Better heat tolerance than pure HF",
                "Steady market demand",
            ],
            challenges: [
                "Variability across cross-breeds",
                "Careful genetics required at purchase",
                "Moderate infrastructure investment",
            ],
        },
    },
    {
        slug: "gir",
        name: "Gir",
        shortName: "Gir",
        tagline: "The regal indigenous breed with prized A2 milk.",
        origin: "Gir forests of Gujarat, India",
        classification: "Indigenous (Bos indicus)",
        color: "Reddish-brown with white patches; distinctive domed forehead",
        image: IMG.gir,
        objectPosition: "40% 40%",
        heroImage: IMG.gir,
        gallery: [IMG.gir, IMG.girAlt, IMG.girAlt2, IMG.motherCalf],
        quickFacts: [
            { label: "Avg. Yield", value: "8–10 L/day" },
            { label: "Milk Fat", value: "4.5%" },
            { label: "Lifespan", value: "10–12 lactations" },
            { label: "Milk Type", value: "A2" },
        ],
        production: {
            avgYield: "8–10 L/day",
            peakYield: "18–22 L/day",
            lactationPeriod: "310 days",
            milkPerLactation: "3,000 – 4,500 L",
            milkFat: "4.5%",
            snf: "9.0%",
            protein: "3.5%",
            suitableFor: ["A2 Milk", "Ghee", "Butter", "Curd", "Ayurvedic Products"],
        },
        economics: {
            calf: "₹30,000 – ₹45,000",
            heifer: "₹65,000 – ₹85,000",
            firstCalving: "₹95,000 – ₹1,25,000",
            lactating: "₹1,10,000 – ₹1,50,000",
            pregnant: "₹1,00,000 – ₹1,35,000",
        },
        feeding: {
            greenFodder: "20 kg/day",
            dryFodder: "5 kg/day",
            concentrate: "4 kg/day",
            mineral: "70 g/day",
            salt: "40 g/day",
            water: "45 L/day",
        },
        feedingCost: [
            { feed: "Green Fodder", qty: "20 kg", daily: 60, monthly: 1800, yearly: 21900 },
            { feed: "Dry Fodder", qty: "5 kg", daily: 50, monthly: 1500, yearly: 18250 },
            { feed: "Concentrate", qty: "4 kg", daily: 120, monthly: 3600, yearly: 43800 },
            { feed: "Mineral Mixture", qty: "70 g", daily: 8, monthly: 240, yearly: 2920 },
            { feed: "Salt", qty: "40 g", daily: 2, monthly: 60, yearly: 730 },
        ],
        totalDailyFeedCost: 240,
        labour: {
            manHours: "1.8 hrs/cow/day",
            breakdown: [
                { task: "Feeding", time: "30 min" },
                { task: "Cleaning", time: "25 min" },
                { task: "Milking", time: "25 min" },
                { task: "Grooming", time: "15 min" },
                { task: "Health Inspection", time: "8 min" },
                { task: "Record Keeping", time: "5 min" },
            ],
        },
        housing: {
            coveredArea: "35 sqft/cow",
            openArea: "80 sqft/cow",
            ventilation: "Natural ventilation sufficient",
            floor: "Rammed earth or grooved concrete",
            drainage: "Sloped 2% toward central drain",
            bedding: "Paddy straw or dry leaves",
        },
        health: {
            diseaseResistance: "Excellent — highly disease resistant",
            commonDiseases: ["Occasional FMD", "Rare mastitis"],
            heatStress: "Very high tolerance",
            tickResistance: "Excellent",
            mastitis: "Very low susceptibility",
            veterinary: "Quarterly veterinary check-up",
        },
        reproduction: {
            firstCalving: "36–42 months",
            gestation: "285 days",
            calvingInterval: "14–16 months",
            fertility: "Good",
            insemination: "AI available; natural common",
            natural: "Widely practiced",
        },
        environment: {
            heat: "Excellent tolerance",
            rain: "Excellent tolerance",
            cold: "Moderate tolerance",
            humidity: "Excellent tolerance",
            regions: "All Karnataka — especially Bagalkot, Vijayapura, Raichur, Kalaburagi",
        },
        business: ["Small Farms", "Medium Farms", "Organic Dairy", "Premium A2 Dairy"],
        ratings: {
            milkYield: 55, milkQuality: 95, feedEfficiency: 82,
            maintenanceCost: 82, diseaseResistance: 94, heatTolerance: 95,
            labourRequirement: 82, profitability: 78, beginnerFriendly: 88,
        },
        recommendation: {
            bestFor: "Premium A2 dairy, ghee brands, organic and low-input farms across Karnataka.",
            notFor: "Farms selling on pure volume-based fluid milk contracts.",
            monthlyOpCost: "₹10,000 – ₹13,000 per cow",
            monthlyMilk: "300 – 400 L",
            expectedROI: "24 – 32 months (higher margin per litre)",
            advantages: [
                "Prized A2 milk with premium pricing",
                "Extraordinary heat and disease tolerance",
                "Long productive life — 10+ lactations",
            ],
            challenges: [
                "Lower total volume",
                "Longer age at first calving",
                "Requires access to A2 or premium markets",
            ],
        },
    },
    {
        slug: "kankrej",
        name: "Kankrej",
        shortName: "Kankrej",
        tagline: "Silver-grey dual-purpose breed of the arid west.",
        origin: "Kutch region, Gujarat & Rajasthan",
        classification: "Indigenous — dual purpose (Bos indicus)",
        color: "Silver-grey to iron-grey with dark points",
        image: IMG.kankrej,
        objectPosition: "62% 38%",
        heroImage: IMG.kankrej,
        gallery: [IMG.kankrej, IMG.kankrejAlt, IMG.calf, IMG.coorg],
        quickFacts: [
            { label: "Avg. Yield", value: "8–10 L/day" },
            { label: "Milk Fat", value: "4.2%" },
            { label: "Purpose", value: "Milk + Draught" },
            { label: "Climate", value: "Arid" },
        ],
        production: {
            avgYield: "8–10 L/day",
            peakYield: "12–14 L/day",
            lactationPeriod: "300 days",
            milkPerLactation: "2,000 – 3,000 L",
            milkFat: "4.2%",
            snf: "8.7%",
            protein: "3.3%",
            suitableFor: ["Drinking Milk", "Ghee", "Curd"],
        },
        economics: {
            calf: "₹25,000 – ₹35,000",
            heifer: "₹55,000 – ₹70,000",
            firstCalving: "₹75,000 – ₹95,000",
            lactating: "₹85,000 – ₹1,10,000",
            pregnant: "₹80,000 – ₹1,00,000",
        },
        feeding: {
            greenFodder: "18 kg/day",
            dryFodder: "5 kg/day",
            concentrate: "3 kg/day",
            mineral: "60 g/day",
            salt: "40 g/day",
            water: "40 L/day",
        },
        feedingCost: [
            { feed: "Green Fodder", qty: "18 kg", daily: 54, monthly: 1620, yearly: 19710 },
            { feed: "Dry Fodder", qty: "5 kg", daily: 50, monthly: 1500, yearly: 18250 },
            { feed: "Concentrate", qty: "3 kg", daily: 90, monthly: 2700, yearly: 32850 },
            { feed: "Mineral Mixture", qty: "60 g", daily: 7, monthly: 210, yearly: 2555 },
            { feed: "Salt", qty: "40 g", daily: 2, monthly: 60, yearly: 730 },
        ],
        totalDailyFeedCost: 203,
        labour: {
            manHours: "1.6 hrs/cow/day",
            breakdown: [
                { task: "Feeding", time: "28 min" },
                { task: "Cleaning", time: "22 min" },
                { task: "Milking", time: "20 min" },
                { task: "Grooming", time: "12 min" },
                { task: "Health Inspection", time: "8 min" },
                { task: "Record Keeping", time: "5 min" },
            ],
        },
        housing: {
            coveredArea: "35 sqft/cow",
            openArea: "80 sqft/cow",
            ventilation: "Natural ventilation sufficient",
            floor: "Rammed earth or grooved concrete",
            drainage: "Sloped 2% toward central drain",
            bedding: "Dry straw",
        },
        health: {
            diseaseResistance: "Excellent",
            commonDiseases: ["Rare occurrences of FMD"],
            heatStress: "Excellent tolerance",
            tickResistance: "Excellent",
            mastitis: "Very low susceptibility",
            veterinary: "Quarterly veterinary check-up",
        },
        reproduction: {
            firstCalving: "36–48 months",
            gestation: "285 days",
            calvingInterval: "14–18 months",
            fertility: "Good under natural conditions",
            insemination: "AI available",
            natural: "Common in rural setups",
        },
        environment: {
            heat: "Excellent tolerance",
            rain: "Good tolerance",
            cold: "Moderate tolerance",
            humidity: "Good tolerance",
            regions: "North Karnataka — Bagalkot, Vijayapura, Raichur, Ballari",
        },
        business: ["Small Farms", "Dual-Purpose Farms", "Organic Dairy"],
        ratings: {
            milkYield: 42, milkQuality: 82, feedEfficiency: 78,
            maintenanceCost: 85, diseaseResistance: 92, heatTolerance: 94,
            labourRequirement: 85, profitability: 68, beginnerFriendly: 82,
        },
        recommendation: {
            bestFor: "Semi-arid North Karnataka farms wanting a hardy dual-purpose animal.",
            notFor: "Volume-focused urban dairies.",
            monthlyOpCost: "₹8,500 – ₹11,000 per cow",
            monthlyMilk: "240 – 300 L",
            expectedROI: "26 – 34 months",
            advantages: [
                "Extremely hardy — thrives on low inputs",
                "Dual-purpose income (milk + draught)",
                "Very low veterinary cost",
            ],
            challenges: [
                "Lower daily yield",
                "Longer calving interval",
                "Limited high-yield genetics",
            ],
        },
    },
    {
        slug: "hallikar",
        name: "Hallikar",
        shortName: "Hallikar",
        tagline: "Karnataka's native heritage draught & household milch cow.",
        origin: "Old Mysore region, Karnataka",
        classification: "Indigenous — primarily draught, moderate milk",
        color: "Grey to dark grey, with a distinct forehead",
        image: IMG.hallikar,
        objectPosition: "60% 40%",
        heroImage: IMG.hallikar,
        gallery: [IMG.hallikar, IMG.hallikarAlt, IMG.hallikarAlt2, IMG.motherCalf],
        quickFacts: [
            { label: "Avg. Yield", value: "3–5 L/day" },
            { label: "Milk Fat", value: "4.5%" },
            { label: "Purpose", value: "Draught + Milk" },
            { label: "Heritage", value: "Karnataka native" },
        ],
        production: {
            avgYield: "3–5 L/day",
            peakYield: "4–5 L/day",
            lactationPeriod: "270 days",
            milkPerLactation: "500 – 800 L",
            milkFat: "4.5%",
            snf: "8.8%",
            protein: "3.4%",
            suitableFor: ["Household Milk", "Ghee", "Cultural & Ritual Use"],
        },
        economics: {
            calf: "₹15,000 – ₹25,000",
            heifer: "₹30,000 – ₹45,000",
            firstCalving: "₹40,000 – ₹55,000",
            lactating: "₹45,000 – ₹65,000",
            pregnant: "₹42,000 – ₹60,000",
        },
        feeding: {
            greenFodder: "15 kg/day",
            dryFodder: "4 kg/day",
            concentrate: "2 kg/day",
            mineral: "50 g/day",
            salt: "30 g/day",
            water: "35 L/day",
        },
        feedingCost: [
            { feed: "Green Fodder", qty: "15 kg", daily: 45, monthly: 1350, yearly: 16425 },
            { feed: "Dry Fodder", qty: "4 kg", daily: 40, monthly: 1200, yearly: 14600 },
            { feed: "Concentrate", qty: "2 kg", daily: 60, monthly: 1800, yearly: 21900 },
            { feed: "Mineral Mixture", qty: "50 g", daily: 6, monthly: 180, yearly: 2190 },
            { feed: "Salt", qty: "30 g", daily: 2, monthly: 60, yearly: 730 },
        ],
        totalDailyFeedCost: 153,
        labour: {
            manHours: "1.2 hrs/cow/day",
            breakdown: [
                { task: "Feeding", time: "22 min" },
                { task: "Cleaning", time: "18 min" },
                { task: "Milking", time: "12 min" },
                { task: "Grooming", time: "10 min" },
                { task: "Health Inspection", time: "6 min" },
                { task: "Record Keeping", time: "4 min" },
            ],
        },
        housing: {
            coveredArea: "30 sqft/cow",
            openArea: "70 sqft/cow",
            ventilation: "Natural ventilation sufficient",
            floor: "Rammed earth acceptable",
            drainage: "Simple gravity drainage",
            bedding: "Dry leaves or straw",
        },
        health: {
            diseaseResistance: "Excellent",
            commonDiseases: ["Very rare FMD"],
            heatStress: "Excellent tolerance",
            tickResistance: "Excellent",
            mastitis: "Extremely low susceptibility",
            veterinary: "Half-yearly veterinary check-up",
        },
        reproduction: {
            firstCalving: "42–54 months",
            gestation: "285 days",
            calvingInterval: "16–20 months",
            fertility: "Good under natural systems",
            insemination: "Limited AI availability",
            natural: "Predominant",
        },
        environment: {
            heat: "Excellent tolerance",
            rain: "Excellent tolerance",
            cold: "Good tolerance",
            humidity: "Excellent tolerance",
            regions: "Mysuru, Mandya, Hassan, Chamarajanagar — its native belt",
        },
        business: ["Small Farms", "Heritage & Conservation Farms", "Cultural Dairy"],
        ratings: {
            milkYield: 22, milkQuality: 78, feedEfficiency: 62,
            maintenanceCost: 92, diseaseResistance: 95, heatTolerance: 96,
            labourRequirement: 92, profitability: 45, beginnerFriendly: 78,
        },
        recommendation: {
            bestFor: "Heritage conservation, cultural farming, household dairy, and organic homesteads.",
            notFor: "Commercial milk-focused dairies.",
            monthlyOpCost: "₹5,500 – ₹7,500 per cow",
            monthlyMilk: "60 – 90 L",
            expectedROI: "Not primarily a commercial ROI breed",
            advantages: [
                "Native Karnataka heritage",
                "Extremely low input requirements",
                "Very long productive life",
            ],
            challenges: [
                "Very low milk yield",
                "Limited market for surplus milk",
                "Suitable primarily for cultural/heritage goals",
            ],
        },
    },
];

export const getBreed = (slug) => breeds.find((b) => b.slug === slug);

export const HERO_IMAGE = "/generated/hero.jpg";
export const ABOUT_IMAGE = "/generated/about.jpg";
export const FEATURE_IMAGE = "/generated/feature.jpg";
